import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './services/authStore'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
import Register from './pages/Register'
import Dashboard from './pages/Dashboard'
import ReceiptUpload from './pages/ReceiptUpload'
import OCRSettings from './pages/OCRSettings'
import ReceiptImageSettings from './pages/ReceiptImageSettings'
import LLMSettings from './pages/LLMSettings'
import LlmCostAccountingAdmin from './pages/LlmCostAccountingAdmin'
import LLMCacheAdmin from './pages/LLMCacheAdmin'
import HeuristicRulesAdmin from './pages/HeuristicRulesAdmin'
import ProductTranslationCacheAdmin from './pages/ProductTranslationCacheAdmin'
import QrExclusionPhrasesAdmin from './pages/QrExclusionPhrasesAdmin'
import ReceiptImageAssetsAdmin from './pages/ReceiptImageAssetsAdmin'
import Receipts from './pages/Receipts'
import ReceiptDetail from './pages/ReceiptDetail'
import Selectors from './pages/Selectors'
import Metrics from './pages/Metrics'
import ProductCategories from './pages/ProductCategories'
import HybridCategorization from './pages/HybridCategorization'
import ReceiptProducts from './pages/ReceiptProducts'
import QRCodeScanner from './pages/QRCodeScanner'
import ReceiptAnalysisQueue from './pages/ReceiptAnalysisQueue'
import ProductAnalysisQueue from './pages/ProductAnalysisQueue'
import ProductTranslationQueue from './pages/ProductTranslationQueue'
import QRCodeProductExtractionQueue from './pages/QRCodeProductExtractionQueue'
import QueueWorkers from './pages/QueueWorkers'
import ExchangeRates from './pages/ExchangeRates'
import Profile from './pages/Profile'
import Translations from './pages/Translations'
import Languages from './pages/Languages'
import UserManagement from './pages/UserManagement'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import VerifyEmail from './pages/VerifyEmail'
import JobDetail from './pages/JobDetail'
import LegalAcceptance from './pages/LegalAcceptance'
import LegalDocuments from './pages/LegalDocuments'
import LegalManagement from './pages/LegalManagement'
import PublicRecognitionsAdmin from './pages/PublicRecognitionsAdmin'
import PublicRecognitionDetail from './pages/PublicRecognitionDetail'
import CurrencyTransitionRulesAdmin from './pages/CurrencyTransitionRulesAdmin'
import CurrencyNormalizationAdmin from './pages/CurrencyNormalizationAdmin'
import PublicApiLimitPoliciesAdmin from './pages/PublicApiLimitPoliciesAdmin'
import PublicApiUsageAdmin from './pages/PublicApiUsageAdmin'
import MobileDevicesAdmin from './pages/MobileDevicesAdmin'
import TelegramMiniAppUsersAdmin from './pages/TelegramMiniAppUsersAdmin'
import MobileAppReleases from './pages/MobileAppReleases'
import MobileAppReleaseDetail from './pages/MobileAppReleaseDetail'
import TelegramMiniAppBridge from './pages/TelegramMiniAppBridge'
import AdminRoute from './components/AdminRoute'
import NotificationProvider from './components/notifications/NotificationProvider'
import { CurrencyCatalogProvider } from './contexts/CurrencyCatalogContext'

function App() {
  const { checkAuthStatus } = useAuthStore()

  // Check auth status on app load
  useEffect(() => {
    checkAuthStatus()
  }, [checkAuthStatus])

  return (
    <CurrencyCatalogProvider>
      <Router>
        <Routes>
          <Route path="/tg-mini/*" element={<TelegramMiniAppBridge />} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email/:id/:hash" element={<VerifyEmail />} />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <NotificationProvider>
                <Layout />
              </NotificationProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/receipts" element={<Receipts />} />
          <Route path="/receipts/:id" element={<ReceiptDetail />} />
          <Route path="/admin/selectors" element={<Selectors />} />
          <Route path="/admin/metrics" element={<Metrics />} />
          <Route path="/categories" element={<ProductCategories />} />
          <Route path="/admin/categories/hybrid" element={<HybridCategorization />} />
          <Route path="/upload" element={<ReceiptUpload />} />
          <Route path="/scan" element={<QRCodeScanner />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/legal/acceptance" element={<LegalAcceptance />} />
          <Route path="/legal/documents" element={<LegalDocuments />} />
          <Route path="/admin/legal" element={<LegalManagement />} />
          <Route path="/admin/ocr-settings" element={<OCRSettings />} />
          <Route path="/admin/receipt-image-limits" element={<AdminRoute><ReceiptImageSettings /></AdminRoute>} />
          <Route path="/admin/llm-settings" element={<LLMSettings />} />
          <Route path="/admin/llm-costs" element={<AdminRoute><LlmCostAccountingAdmin /></AdminRoute>} />
          <Route path="/admin/llm-cache" element={<LLMCacheAdmin />} />
          <Route path="/admin/categories/hybrid/cache" element={<ProductTranslationCacheAdmin />} />
          <Route path="/admin/categories/exclusion-phrases" element={<QrExclusionPhrasesAdmin />} />
          <Route path="/admin/receipt-image-assets" element={<ReceiptImageAssetsAdmin />} />
          <Route path="/admin/exchange-rates" element={<AdminRoute><ExchangeRates /></AdminRoute>} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/languages" element={<Languages />} />
          <Route path="/receipt-products" element={<ReceiptProducts />} />
          <Route path="/queue/receipt-analysis" element={<ReceiptAnalysisQueue />} />
          <Route path="/queue/product-analysis" element={<ProductAnalysisQueue />} />
          <Route path="/queue/product-translation" element={<ProductTranslationQueue />} />
          <Route path="/queue/qrcode-product-extraction" element={<QRCodeProductExtractionQueue />} />
          <Route path="/admin/heuristic-rules" element={<HeuristicRulesAdmin />} />
          <Route path="/admin/queue-workers" element={<QueueWorkers />} />
          <Route path="/admin/jobs/:id" element={<JobDetail />} />
          <Route path="/admin/translations" element={<Translations />} />
          <Route path="/admin/public-recognitions" element={<AdminRoute><PublicRecognitionsAdmin /></AdminRoute>} />
          <Route path="/admin/public-recognitions/:id" element={<AdminRoute><PublicRecognitionDetail /></AdminRoute>} />
          <Route path="/admin/currency-transition-rules" element={<AdminRoute><CurrencyTransitionRulesAdmin /></AdminRoute>} />
          <Route path="/admin/currency-normalization" element={<AdminRoute><CurrencyNormalizationAdmin /></AdminRoute>} />
          <Route path="/admin/public-api/limits" element={<AdminRoute><PublicApiLimitPoliciesAdmin /></AdminRoute>} />
          <Route path="/admin/public-api/usage" element={<AdminRoute><PublicApiUsageAdmin /></AdminRoute>} />
          <Route path="/admin/mobile-devices" element={<AdminRoute><MobileDevicesAdmin /></AdminRoute>} />
          <Route path="/admin/telegram-mini-app-users" element={<AdminRoute><TelegramMiniAppUsersAdmin /></AdminRoute>} />
          <Route path="/admin/mobile/releases" element={<AdminRoute><MobileAppReleases /></AdminRoute>} />
          <Route path="/admin/mobile/releases/:id" element={<AdminRoute><MobileAppReleaseDetail /></AdminRoute>} />

          {/* Legacy path redirects */}
          <Route path="/translations" element={<Navigate to="/admin/translations" replace />} />
          <Route path="/languages" element={<Navigate to="/admin/languages" replace />} />
          <Route path="/exchange-rates" element={<Navigate to="/admin/exchange-rates" replace />} />
          <Route path="/llm-settings" element={<Navigate to="/admin/llm-settings" replace />} />
          <Route path="/ocr-settings" element={<Navigate to="/admin/ocr-settings" replace />} />
          <Route path="/receipt-image-limits" element={<Navigate to="/admin/receipt-image-limits" replace />} />
          <Route path="/users" element={<Navigate to="/admin/users" replace />} />
          <Route path="/categories/hybrid" element={<Navigate to="/admin/categories/hybrid" replace />} />
        </Route>

        {/* Redirect unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </CurrencyCatalogProvider>
  )
}

export default App
